import React, { useCallback, useEffect, useState } from 'react';

import { ResponsiveSizes, VideoPlayerProps } from 'features/player/types';
import styled from 'styled-components';

const PlayerWrapper = styled('div')<{
  width: ResponsiveSizes;
  height: ResponsiveSizes;
  maxWidth: ResponsiveSizes;
  maxHeight: ResponsiveSizes;
}>`
  ${({ width, theme: { mediaQueries } }) => {
    if (typeof width === 'object') {
      let response = '';

      if (width.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            width: ${typeof width.m === 'number' ? `${width.m}px` : width.m};
          }
        `;
      }

      if (width.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            width: ${typeof width.t === 'number' ? `${width.t}px` : width.t};
          }
        `;
      }

      if (width.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            width: ${typeof width.d === 'number' ? `${width.d}px` : width.d};
          }
        `;
      }

      return response;
    } else {
      return `
        width: ${typeof width === 'number' ? `${width}px` : width};
      `;
    }
  }};

  ${({ height, theme: { mediaQueries } }) => {
    if (typeof height === 'object') {
      let response = '';

      if (height.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            height: ${typeof height.m === 'number' ? `${height.m}px` : height.m};
          }
        `;
      }

      if (height.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            height: ${typeof height.t === 'number' ? `${height.t}px` : height.t};
          }
        `;
      }

      if (height.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            height: ${typeof height.d === 'number' ? `${height.d}px` : height.d};
          }
        `;
      }

      return response;
    } else {
      return `
        height: ${typeof height === 'number' ? `${height}px` : height};
      `;
    }
  }};

  ${({ maxWidth, theme: { mediaQueries } }) => {
    if (typeof maxWidth === 'object') {
      let response = '';

      if (maxWidth.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            max-width: ${typeof maxWidth.m === 'number' ? `${maxWidth.m}px` : maxWidth.m};
          }
        `;
      }

      if (maxWidth.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            max-width: ${typeof maxWidth.t === 'number' ? `${maxWidth.t}px` : maxWidth.t};
          }
        `;
      }

      if (maxWidth.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            max-width: ${typeof maxWidth.d === 'number' ? `${maxWidth.d}px` : maxWidth.d};
          }
        `;
      }

      return response;
    } else {
      return `
        max-width: ${typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth};
      `;
    }
  }};

  ${({ maxHeight, theme: { mediaQueries } }) => {
    if (typeof maxHeight === 'object') {
      let response = '';

      if (maxHeight.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            max-height: ${typeof maxHeight.m === 'number' ? `${maxHeight.m}px` : maxHeight.m};
          }
        `;
      }

      if (maxHeight.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            max-height: ${typeof maxHeight.t === 'number' ? `${maxHeight.t}px` : maxHeight.t};
          }
        `;
      }

      if (maxHeight.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            max-height: ${typeof maxHeight.d === 'number' ? `${maxHeight.d}px` : maxHeight.d};
          }
        `;
      }

      return response;
    } else {
      return `
        max-height: ${typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight};
      `;
    }
  }};
`;

const Player = styled.iframe`
  border: 0;
`;

const PandaPlayer = ({
  externalVideoProviderIdOrUrl,
  protectedVideoPlaybackInfo,
  startInSecond,
  onTimeUpdate,
  onEnd,

  width = '100%',
  height = '100%',
  maxWidth = 'inherit',
  maxHeight = 'inherit',
}: VideoPlayerProps) => {
  const [player, setPlayer] = useState<any>(undefined!);
  const playerRef = React.useRef<HTMLDivElement>(null);

  const loadPlayer = useCallback(() => {
    window.playerContainer = playerRef.current;

    const player = new window.PandaPlayer(`panda-${externalVideoProviderIdOrUrl}`, {
      onReady: () => {
        player.onEvent(({ message, currentTime, ...props }: any) => {
          if (message === 'panda_timeupdate') {
            if (onTimeUpdate) onTimeUpdate(currentTime);
          }

          if (message === 'panda_ended') {
            if (onEnd) onEnd(player.duration);
          }
        });
      },
      onError: (event: any) => {
        console.error('Player onError', event);
      },
    });

    setPlayer(player);
  }, [externalVideoProviderIdOrUrl]);

  useEffect(() => {
    if (!window.pandascripttag) {
      const playerScript = document.createElement('script');
      playerScript.src = 'https://player.pandavideo.com.br/api.v2.js';

      document.body.appendChild(playerScript);

      playerScript.addEventListener('load', () => {
        return loadPlayer();
      });
    } else {
      loadPlayer();
    }
  }, [externalVideoProviderIdOrUrl]);

  useEffect(() => {
    if (player && startInSecond) {
      player.setCurrentTime(startInSecond);
      player.play();
    }
  }, [player, startInSecond]);

  const src = protectedVideoPlaybackInfo?.split('?');

  if (!src) return <></>;

  const query = Object.fromEntries(new URLSearchParams(src[1]));

  const params: Record<string, any> = { ...query, autoplay: false, preload: false };

  var queryString = Object.keys(params)
    .map((key) => key + '=' + params[key])
    .join('&');

  return (
    <PlayerWrapper width={width} height={height} maxWidth={maxWidth} maxHeight={maxHeight}>
      <Player
        title=""
        id={`panda-${externalVideoProviderIdOrUrl}`}
        src={`${src[0]}?${queryString}`}
        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"
        allowFullScreen
        width="100%"
        height="100%"
      />
    </PlayerWrapper>
  );
};

export default PandaPlayer;
